import { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const useTimeAgo = (date: Date | undefined) => {
  const intl = useIntl();

  const getTimeAgo = useCallback(
    (date: Date): string => {
      const now = new Date();
      const timeDiff = now.getTime() - date.getTime();

      const messages = defineMessages({
        timeAgo: {
          defaultMessage: '{time} ago',
          id: 'dictonary.timeAgo'
        }
      });

      // Convert milliseconds to different units
      const seconds = Math.floor(timeDiff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);
      const months = Math.floor(days / 30);
      const years = Math.floor(days / 365);

      if (years >= 1) {
        return intl.formatMessage(messages.timeAgo, { time: `${years} year${years > 1 ? 's' : ''}` });
      } else if (months >= 1) {
        return intl.formatMessage(messages.timeAgo, { time: `${months} month${months > 1 ? 's' : ''}` });
      } else if (weeks >= 1) {
        return intl.formatMessage(messages.timeAgo, { time: `${weeks} week${weeks > 1 ? 's' : ''}` });
      } else if (days >= 1) {
        return intl.formatMessage(messages.timeAgo, { time: `${days} day${days > 1 ? 's' : ''}` });
      } else if (hours >= 1) {
        return intl.formatMessage(messages.timeAgo, { time: `${hours} hour${hours > 1 ? 's' : ''}` });
      } else if (minutes >= 1) {
        return intl.formatMessage(messages.timeAgo, { time: `${minutes} minute${minutes > 1 ? 's' : ''}` });
      } else {
        return intl.formatMessage({ id: 'dictionary.justNow' });
      }
    },
    [intl]
  );

  const [timeAgo, setTimeAgo] = useState(date ? getTimeAgo(date) : '');

  useEffect(() => {
    if (!date) return;

    const updateElapsedTime = () => {
      const newTimeAgo = getTimeAgo(date);
      setTimeAgo(newTimeAgo);
    };

    const interval = setInterval(updateElapsedTime, 35000);

    return () => {
      clearInterval(interval);
    };
  }, [date, getTimeAgo, intl]);

  useEffect(() => {
    if (!date) return;

    const newTimeAgo = getTimeAgo(date);
    setTimeAgo(newTimeAgo);
  }, [date, getTimeAgo, intl]);

  return timeAgo;
};

export default useTimeAgo;
